import { useMemo } from 'react';

import { type FastshiftMigrationPlan } from '@wac-web/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client.types';
import { useIntl } from 'react-intl';

import Heading from '@atlaskit/heading';
import Lozenge from '@atlaskit/lozenge';
import { Text } from '@atlaskit/primitives/compiled';

import { FASTSHIFT_PRODUCT_VARIANTS } from '@/common/fastshift-product-icon/src/fastshift-product-icon';
import {
	DEFAULT_CONTROLS_PAYLOAD,
	FastshiftTable,
	type TableHeads,
	type TableRows,
} from '@/common/fastshift-table';

import { messages } from './messages';
import { MigrationPlanSiteDetails } from './migration-plan-site-details';
import {
	formatMigrationPlanElapsedTime,
	getMigrationPlanElapsedSeconds,
	getMigrationPlanStageMessage,
	getMigrationPlanStatusConfig,
} from './migration-plans-display-utils';

const HEADER_CELLS = [
	{ key: 'source', message: messages.sourceColumnHeading, width: 22 },
	{ key: 'destination', message: messages.destinationColumnHeading, width: 22 },
	{ key: 'migration-plan-status', message: messages.statusColumnHeading, width: 12 },
	{ key: 'migration-plan-name', message: messages.migrationPlanNameColumnHeading, width: 16 },
	{ key: 'migration-plan-stage', message: messages.migrationPlanStageColumnHeading, width: 8 },
	{
		key: 'migration-plan-elapsed-time',
		message: messages.migrationPlanElapsedTimeColumnHeading,
		width: 10,
	},
	{
		key: 'migration-plan-created-at',
		message: messages.migrationPlanCreatedAtColumnHeading,
		width: 10,
	},
];

type MigrationPlansTableProps = {
	plans: FastshiftMigrationPlan[];
};

// TODO: (follow-up) add table toolbar, filters and sorting https://hello.jira.atlassian.cloud/browse/SXP-5677
export const MigrationPlansTable = ({ plans }: MigrationPlansTableProps) => {
	const { formatDate, formatMessage } = useIntl();

	const head = useMemo<TableHeads>(
		() => ({
			cells: HEADER_CELLS.map(({ key, message, width }) => ({
				key,
				content: (
					<Heading size="xxsmall" as="span">
						{formatMessage(message)}
					</Heading>
				),
				width,
			})),
		}),
		[formatMessage],
	);

	const rows = useMemo<TableRows>(
		() =>
			plans.map((plan) => {
				const statusConfig = getMigrationPlanStatusConfig(plan.status);
				const elapsedSeconds = getMigrationPlanElapsedSeconds(plan);
				const cells: TableRows[number]['cells'] = [
					{
						key: 'source',
						content: (
							<MigrationPlanSiteDetails
								product={plan.source.product}
								baseUrl={plan.source.baseUrl}
								variant={FASTSHIFT_PRODUCT_VARIANTS.SOURCE}
							/>
						),
					},
					{
						key: 'destination',
						content: (
							<MigrationPlanSiteDetails
								product={plan.destination.product}
								baseUrl={plan.destination.baseUrl}
								variant={FASTSHIFT_PRODUCT_VARIANTS.DESTINATION}
							/>
						),
					},
					{
						key: 'migration-plan-status',
						content: (
							<Lozenge
								appearance={statusConfig.appearance}
								testId={`migration-plan-status-${plan.id}`}
							>
								{formatMessage(statusConfig.message)}
							</Lozenge>
						),
					},
					{
						key: 'migration-plan-name',
						content: <Text maxLines={1}>{plan.name || '-'}</Text>,
					},
					{
						key: 'migration-plan-stage',
						content: (
							<Text maxLines={1}>{formatMessage(getMigrationPlanStageMessage(plan.phase))}</Text>
						),
					},
					{
						key: 'migration-plan-elapsed-time',
						content: (
							<Text maxLines={1}>
								{elapsedSeconds === null
									? '-'
									: formatMigrationPlanElapsedTime(elapsedSeconds, formatMessage)}
							</Text>
						),
					},
					{
						key: 'migration-plan-created-at',
						content: (
							<Text maxLines={1}>
								{plan.created
									? formatDate(plan.created, {
											year: 'numeric',
											month: 'short',
											day: 'numeric',
										})
									: '-'}
							</Text>
						),
					},
				];

				return {
					key: plan.id,
					cells,
				};
			}),
		[formatDate, formatMessage, plans],
	);

	return (
		<FastshiftTable
			id="organization-migration-plans"
			testId="organization-migration-plans"
			tableCaption={formatMessage(messages.tableCaption)}
			isTableLoading={false}
			isTableError={false}
			errorComponent={null} // TODO: add error, loading and empty state when API is implemented https://hello.jira.atlassian.cloud/browse/SXP-5383
			head={head}
			rows={rows}
			controls={{
				// TODO: (follow-up) update pagination to server side w cursor, using hardcodedmock values for now https://hello.jira.atlassian.cloud/browse/SXP-5383
				pagination: {
					page: 0,
					size: 20,
					totalElements: plans.length,
					totalPages: 1,
					hasNext: false,
					hasPrevious: false,
					sorted: false,
				},
				controls: DEFAULT_CONTROLS_PAYLOAD,
				onSetControlsPayload: () => {},
			}}
		/>
	);
};
